import { ApplicationRef, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ResponseInterface } from '../interfaces/response';
import { UserService } from './user.service';
import { ApiService } from './api.service';
import { Observable, Subject } from 'rxjs';
import { ContactSaveEvent } from '../interfaces/contactSaveEvent';
import { SaveEventAction } from '../enums/saveEventAction';
import { Contact, UserType } from '../enums/constants';

@Injectable({
  providedIn: 'root',
})
export class ContactService {
  user: any = null;
  usertype: any = UserType.CONTACT;
  contactSaveEvents$: Observable<ContactSaveEvent>;

  private contactSaveAction: Subject<ContactSaveEvent>;

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private apiService: ApiService,
    private application: ApplicationRef,
  ) {
    this.contactSaveAction = new Subject();
    this.contactSaveEvents$ = this.contactSaveAction.asObservable();
  }

  deniedInvite(reqparams, responseInterface: ResponseInterface<any>) {
    return this.http.post<any>('/api/decline-contact', reqparams).subscribe(
      (res: any) => {
        responseInterface.getResult(res);
      },
      (err) => {
        responseInterface.getError(err);
      }
    );
  }

  getContactById(contactId) {
    return this.apiService.allContacts.find(contact => contact.id === contactId);
  }

  addToCachedContacts(contact, internalSync = false) {
    if (this.apiService.allContacts.filter(existingContact => existingContact.id === contact.id).length > 0) {
      return;
    }

    this.apiService.allContacts.push(contact);
    if (contact.type === Contact.TYPE_DESIGNEE) {
      this.apiService.designeeControl.push(contact);
    } else if (contact.type === Contact.TYPE_NONE) {
      this.apiService.contactsRoleNone.push(contact);
    }
    this.application.tick();

    if (!internalSync) {
      this.contactSaveAction.next({
        contact: contact,
        action: SaveEventAction.add
      });
    }
  }

  editIntoCachedContacts(updatedContact) {
    this.doUpdate(this.apiService.allContacts, updatedContact);
    this.doUpdate(this.apiService.designeeControl, updatedContact);
    this.doUpdate(this.apiService.contactsRoleNone, updatedContact);
    this.placeIntoCorrectList(updatedContact);

    this.contactSaveAction.next({
      contact: updatedContact,
      action: SaveEventAction.edit
    });
  }

  private doUpdate(contacts: any[], updatedContact) {
    const index = contacts.findIndex(contact => contact.id === updatedContact.id);
    if (index >= 0) {
      for (let key in updatedContact) {
        if (key === 'disabled') {
          continue;
        }
        contacts[index][key] = updatedContact[key];
      }
    }
  }

  private placeIntoCorrectList(updatedContact) {
    this.deleteFromCachedContacts(updatedContact, true);
    this.addToCachedContacts(updatedContact, true);
  }

  deleteFromCachedContacts(contact, internalSync = false) {
    const contactId = contact.id;
    this.spliceArray(this.apiService.allContacts, contactId);
    this.spliceArray(this.apiService.designeeControl, contactId);
    this.spliceArray(this.apiService.contactsRoleNone, contactId);
    this.application.tick();

    if (!internalSync) {
      this.clearContactReferences(contact);
      this.contactSaveAction.next({
        contact: contact,
        action: SaveEventAction.delete
      });
    }
  }

  private spliceArray(contacts: any[], contactId: number) {
    const index = contacts.findIndex(contact => contact.id === contactId);
    if (index >= 0)
      contacts.splice(index, 1);
  }

  getContactRoles(contact, vaultUser = null): string[] {
    const roles: string[] = [];
    if (
      (this.userService.usertype === UserType.CONTACT && contact.userId !== this.userService.user.openedVaultForUser?.id) ||
      (this.userService.usertype === UserType.USER && contact.userId !== this.userService.user.id) ||
      (this.userService.usertype === UserType.ADMIN && contact.userId !== vaultUser?.id)
    ) {
      return roles;
    }

    if (this.isWillExecutor(contact)) {
      roles.push(Contact.ACTOR_EXECUTOR);
    }

    if (this.isPrimaryTrustee(contact)) {
      roles.push(Contact.ACTOR_PRIMARY_TRUSTEE);
    }

    if (this.isSecondaryTrustee(contact)) {
      roles.push(Contact.ACTOR_SECONDARY_TRUSTEE);
    }

    if (vaultUser && this.isSpouse(vaultUser, contact)) {
      roles.push(Contact.ACTOR_SPOUSE);
    }

    if (this.isPrimaryBeneficiary(contact)) {
      roles.push(Contact.ACTOR_PRIMARY_BENEFICIARY);
    }

    if (this.isSecondaryBeneficiary(contact)) {
      roles.push(Contact.ACTOR_SECONDARY_BENEFICIARY);
    }

    if (this.hasReferenceToPolicies(contact)) {
      roles.push(Contact.ACTOR_INSURANCE_BENEFICIARY);
    }

    return roles;
  }

  getContactActsToShow(contactRoles: string[]) {
    return contactRoles.map((role) => {
      if (role.endsWith('trustee')) {
        return role === Contact.ACTOR_PRIMARY_TRUSTEE ? 'Primary Trustee' : 'Secondary Trustee';
      } else if (role === Contact.ACTOR_PRIMARY_BENEFICIARY || role === Contact.ACTOR_SECONDARY_BENEFICIARY) {
        return role === Contact.ACTOR_PRIMARY_BENEFICIARY ? 'Primary Retirement Acct Beneficiary' : 'Secondary Retirement Acct Beneficiary';
      } else if (role === Contact.ACTOR_EXECUTOR) {
        return 'Will Executor';
      } else if (role === Contact.ACTOR_INSURANCE_BENEFICIARY) {
        return 'Insurance Beneficiary';
      } else {
        return role[0].toUpperCase() + role.substring(1);
      }
    }).map((roleText) => roleText + ';').join(' ');
  }

  isSpouse(user, contact) {
    return user.married && user.marriedTo === contact.id;
  }

  isWillExecutor(contact, excludeId = -1) {
    const will = this.apiService.wills.find(will => will.executor === contact.id && will.id !== excludeId);
    return !!will;
  }

  isPrimaryTrustee(contact, excludeId = -1) {
    return this.apiService.trusts.filter(trust => trust.id !== excludeId && trust.primaryTrustees && trust.primaryTrustees.includes(contact.id)).length > 0;
  }

  isSecondaryTrustee(contact, excludeId = -1) {
    return this.apiService.trusts.filter(trust => trust.id !== excludeId && trust.secondaryTrustees && trust.secondaryTrustees.includes(contact.id)).length > 0;
  }

  isPrimaryBeneficiary(contact, excludeId = -1) {
    return this.apiService.retirementAccounts.filter(retirementAccount =>
      retirementAccount.id !== excludeId &&
      retirementAccount.primaryBeneficiaries && retirementAccount.primaryBeneficiaries.includes(contact.id)
    ).length > 0;
  }

  isSecondaryBeneficiary(contact, excludeId = -1) {
    return this.apiService.retirementAccounts.filter(retirementAccount =>
      retirementAccount.id !== excludeId &&
      retirementAccount.secondaryBeneficiaries && retirementAccount.secondaryBeneficiaries.includes(contact.id)
    ).length > 0;
  }

  hasReferenceToPolicies(contact, excludeId = -1) {
    for (let insurance of this.apiService.insurances) {
      if (!insurance.policies) {
        continue;
      }
      for (let policy of insurance.policies) {
        if (excludeId > 0 && policy?.id === excludeId) {
          continue;
        }
        if (policy?.beneficiaries?.includes(contact.id)) {
          return true;
        }
      }
    }

    return false;
  }

  clearContactReferences(deletedContact) {
    if (this.isSpouse(this.userService.user, deletedContact)) {
      this.userService.user.marriedTo = 0;
      this.userService.user.spouse = null;
    }

    if (this.isWillExecutor(deletedContact)) {
      this.apiService.wills[0].executor = 0;
    }

    if (this.isPrimaryTrustee(deletedContact)) {
      this.apiService.trusts.filter(trust => trust.primaryTrustees && trust.primaryTrustees.includes(deletedContact.id)).forEach(trust => {
        trust.primaryTrustees = trust.primaryTrustees.filter(trusteeId => trusteeId !== deletedContact.id);
      });
    }

    if (this.isSecondaryTrustee(deletedContact)) {
      this.apiService.trusts.filter(trust => trust.secondaryTrustees && trust.secondaryTrustees.includes(deletedContact.id)).forEach(trust => {
        trust.secondaryTrustees = trust.secondaryTrustees.filter(trusteeId => trusteeId !== deletedContact.id);
      });
    }

    if (this.isPrimaryBeneficiary(deletedContact)) {
      this.apiService.retirementAccounts.forEach(retirementAccount => {
        retirementAccount.primaryBeneficiaries = retirementAccount.primaryBeneficiaries.filter((beneficiaryId: number) => beneficiaryId !== deletedContact.id);
      });
    }

    if (this.isSecondaryBeneficiary(deletedContact)) {
      this.apiService.retirementAccounts.forEach(retirementAccount => {
        retirementAccount.secondaryBeneficiaries = retirementAccount.secondaryBeneficiaries.filter((beneficiaryId: number) => beneficiaryId !== deletedContact.id);
      });
    }

    this.apiService.loginAccounts.forEach(item => item.permissionList = item.permissionList?.filter(contactId => contactId !== deletedContact.id));
    this.apiService.additionalDocs.forEach(item => item.permissionList = item.permissionList?.filter(contactId => contactId !== deletedContact.id));
    this.apiService.banks.forEach(item => item.permissionList = item.permissionList?.filter(contactId => contactId !== deletedContact.id));
    this.apiService.insurances.forEach(item => item.permissionList = item.permissionList?.filter(contactId => contactId !== deletedContact.id));
    this.apiService.multimedias.forEach(item => item.permissionList = item.permissionList?.filter(contactId => contactId !== deletedContact.id));
    this.apiService.privateInvestments.forEach(item => item.permissionList = item.permissionList?.filter(contactId => contactId !== deletedContact.id));
    this.apiService.realEstates.forEach(item => item.permissionList = item.permissionList?.filter(contactId => contactId !== deletedContact.id));
    this.apiService.trusts.forEach(item => item.permissionList = item.permissionList?.filter(contactId => contactId !== deletedContact.id));
    this.apiService.wills.forEach(item => item.permissionList = item.permissionList?.filter(contactId => contactId !== deletedContact.id));
    this.apiService.retirementAccounts.forEach(item => item.permissionList = item.permissionList?.filter(contactId => contactId !== deletedContact.id));

    for(let cardName in this.apiService.cardsMinimumData) {
      for(let card of this.apiService.cardsMinimumData[cardName]) {
        if(card && card.permissionList) {
          card.permissionList = card.permissionList.filter(contactId => contactId !== deletedContact.id);
        }
      }
    }

    if (this.hasReferenceToPolicies(deletedContact)) {
      this.apiService.insurances.forEach(insurance => {
        if (insurance.policies) {
          insurance.policies.forEach(policy => {
            if (policy.beneficiaries) {
              policy.beneficiaries = policy.beneficiaries.filter(contactId => contactId !== deletedContact.id)
            }
          });
        }
      });
    }
  }

  saveAssociatedContacts(data: { cardItems: any[], associatedContacts: any[], associatedLegalFinancialContacts: any[] }, usertype: UserType) {
    if (usertype !== UserType.CONTACT) {
      return;
    }

    data.associatedContacts?.forEach(contact => this.addToCachedContacts(contact));
    data.associatedLegalFinancialContacts?.forEach(legalFinancialContact => {
      if (!this.apiService.legalFinancialContacts.find(lfc => lfc.id === legalFinancialContact.id)) {
        this.apiService.legalFinancialContacts.push(legalFinancialContact);
      }
    });
  }

  isContactDeletable(
    contact,
    deletingAsActor: Contact.ACTOR_SPOUSE | Contact.ACTOR_EXECUTOR | Contact.ACTOR_PRIMARY_TRUSTEE | Contact.ACTOR_SECONDARY_TRUSTEE | Contact.ACTOR_PRIMARY_BENEFICIARY | Contact.ACTOR_SECONDARY_BENEFICIARY | Contact.ACTOR_INSURANCE_BENEFICIARY | null = null,
    deletingFromCardItemId = -1
  ) {
    if (contact.type !== Contact.TYPE_NONE) {
      return false;
    }

    return !this.hasAnyNonPermissionReferences(contact, deletingAsActor, deletingFromCardItemId);
  }

  hasAnyNonPermissionReferences(contact, deletingAsActor, ignoreReferenceCardItemId = -1) {
    let hasReference = false;
    let excludeCardItemId = -1;
    hasReference = hasReference || (deletingAsActor !== Contact.ACTOR_SPOUSE && this.isSpouse(this.userService.user, contact));

    excludeCardItemId = (deletingAsActor === Contact.ACTOR_EXECUTOR) ? ignoreReferenceCardItemId : -1;
    hasReference = hasReference || this.isWillExecutor(contact, excludeCardItemId);

    excludeCardItemId = (deletingAsActor === Contact.ACTOR_PRIMARY_TRUSTEE) ? ignoreReferenceCardItemId : -1;
    hasReference = hasReference || this.isPrimaryTrustee(contact, excludeCardItemId);

    excludeCardItemId = (deletingAsActor === Contact.ACTOR_SECONDARY_TRUSTEE) ? ignoreReferenceCardItemId : -1;
    hasReference = hasReference || this.isSecondaryTrustee(contact, excludeCardItemId);

    excludeCardItemId = (deletingAsActor === Contact.ACTOR_PRIMARY_BENEFICIARY) ? ignoreReferenceCardItemId : -1;
    hasReference = hasReference || this.isPrimaryBeneficiary(contact, excludeCardItemId);

    excludeCardItemId = (deletingAsActor === Contact.ACTOR_SECONDARY_BENEFICIARY) ? ignoreReferenceCardItemId : -1;
    hasReference = hasReference || this.isSecondaryBeneficiary(contact, excludeCardItemId);

    excludeCardItemId = (deletingAsActor === Contact.ACTOR_INSURANCE_BENEFICIARY) ? ignoreReferenceCardItemId : -1;
    hasReference = hasReference || this.hasReferenceToPolicies(contact, excludeCardItemId);

    return hasReference;
  }
}
